import { ENV } from "@/shared/config/env";

const EXTENSION_ID = ENV.extensionId ?? "";

type TokenClearedMessage = {
  action: "TOKEN_CLEARED";
  reason: string;
  timestamp: number;
};

const EXTENSION_PING_TYPE = "PINBOX_EXTENSION_PING" as const;
const EXTENSION_PONG_TYPE = "PINBOX_EXTENSION_PONG" as const;
const EXTENSION_CAPABILITIES = [
  "save-page",
  "save-selection",
  "save-image",
  "capture-screenshot",
] as const;

export type ExtensionCapability = (typeof EXTENSION_CAPABILITIES)[number];

export interface ExtensionInfo {
  version: string;
  capabilities: ExtensionCapability[];
}

export interface ExtensionRuntime {
  lastError?: { message: string };
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback: (response: unknown) => void,
  ) => void;
}

interface DetectExtensionOptions {
  extensionId?: string;
  requestId?: string;
  runtime?: ExtensionRuntime | null;
  timeoutMs?: number;
}

// Chrome extension API 类型检查
declare const chrome: {
  runtime: ExtensionRuntime;
} | undefined;

const canNotifyExtension = () =>
  typeof window !== "undefined" &&
  typeof chrome !== "undefined" &&
  chrome?.runtime !== undefined &&
  typeof chrome.runtime.sendMessage === "function";

function getExtensionRuntime(): ExtensionRuntime | null {
  return canNotifyExtension() ? chrome!.runtime : null;
}

function createRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseExtensionInfo(
  response: unknown,
  requestId: string,
): ExtensionInfo | null {
  if (!response || typeof response !== "object") return null;

  const candidate = response as {
    type?: unknown;
    requestId?: unknown;
    version?: unknown;
    capabilities?: unknown;
  };
  if (
    candidate.type !== EXTENSION_PONG_TYPE ||
    candidate.requestId !== requestId ||
    typeof candidate.version !== "string" ||
    candidate.version.length === 0 ||
    !Array.isArray(candidate.capabilities)
  ) {
    return null;
  }

  const capabilities = candidate.capabilities.filter(
    (capability): capability is ExtensionCapability =>
      EXTENSION_CAPABILITIES.includes(capability as ExtensionCapability),
  );
  if (capabilities.length !== candidate.capabilities.length) return null;

  return { version: candidate.version, capabilities };
}

export function detectExtension(
  options: DetectExtensionOptions = {},
): Promise<ExtensionInfo | null> {
  const extensionId = options.extensionId ?? EXTENSION_ID;
  const runtime = options.runtime === undefined
    ? getExtensionRuntime()
    : options.runtime;
  const requestId = options.requestId ?? createRequestId();
  const timeoutMs = options.timeoutMs ?? 1000;

  if (!extensionId || !runtime) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: ExtensionInfo | null) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      resolve(result);
    };
    const timeout = globalThis.setTimeout(() => finish(null), timeoutMs);

    try {
      runtime.sendMessage(
        extensionId,
        { type: EXTENSION_PING_TYPE, requestId },
        (response) => {
          if (runtime.lastError) {
            finish(null);
            return;
          }
          finish(parseExtensionInfo(response, requestId));
        },
      );
    } catch {
      finish(null);
    }
  });
}

/**
 * 通知浏览器扩展清除缓存的 token。
 * 
 * 通信方式：网页 → Service Worker（直接通信）
 * 
 * 使用 chrome.runtime.sendMessage + externally_connectable 实现：
 * 1. 扩展在 manifest.json 中配置 externally_connectable.matches 允许本网站通信
 * 2. 网页通过 chrome.runtime.sendMessage(extensionId, message) 直接发送消息
 * 3. Service Worker 通过 chrome.runtime.onMessageExternal 接收消息
 * 
 * 优势：无需 Content Script 作为桥梁，直接通信更简单高效
 * 
 * @param reason 登出原因，如 "user-logout" 或 "token-expired"
 */
export const notifyExtensionTokenCleared = (reason: string = "user-logout") => {
  if (!canNotifyExtension()) {
    console.warn("Chrome extension API not available");
    return;
  }

  if (!EXTENSION_ID) {
    console.warn("Extension ID not configured. Set VITE_EXTENSION_ID in .env");
    return;
  }

  const message: TokenClearedMessage = {
    action: "TOKEN_CLEARED",
    reason,
    timestamp: Date.now(),
  };

  chrome!.runtime.sendMessage(EXTENSION_ID, message, (response) => {
    if (chrome!.runtime.lastError) {
      console.warn("Failed to notify extension:", chrome!.runtime.lastError.message);
    } else {
      console.log("Extension notified successfully:", response);
    }
  });
};

export const extensionBridgeConstants = {
  EXTENSION_ID,
  TOKEN_CLEARED_ACTION: "TOKEN_CLEARED" as const,
};
